import { FormattedMessage } from 'react-intl';
import { getFormattedName, isMinor } from 'pages/AffordaHousehold/utils';
import {
  AlertInfoIcon,
  MoneyIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { YES_NO_OPTIONS } from 'utils/constants';
import PATHS, {
  IDENTIFYING_DETAILS_PATHS,
  RESIDENTIAL_HISTORY_PATHS,
} from 'constants/paths';
import { validateNoSpecialChars } from 'utils/validateNoSpecialChars';
import {
  getExplainTheSituationLengthError,
  getExplainTheSituationWatchFields,
} from './utils.explainTheSituation';
import {
  Box,
  List,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

export const HUD_CITIZENSHIP_FIELD_NAMES = {
  affordableCitizenStatusId: 'affordableCitizenStatusId',
  ssnExceptionsExplanation: 'ssnExceptionsExplanation',
  affordableSSNExceptionId: 'affordableSSNExceptionId',
};

export const MAX_SSN_LENGTH = 9;

export const MESSAGE_IDS = {
  BACKGROUND: {
    TITLE: 'afforda.identifyingDetails.background.title',
  },
  IDENTIFICATION_ALTERNATE: {
    TITLE: 'afforda.identifyingDetails.identificationAlternate.title',
  },
  RESIDENTIAL_HISTORY: {
    TITLE: 'afforda.identifyingDetails.residentialHistory.title',
  },
};

export const IDENTIFICATION_FIELD_NAMES = {
  socialSecurity: 'socialSecurity',
};

export const IDENTIFICATION_ALTERNATE_FIELD_NAMES = {
  alternateId: 'alternateId',
  passport: 'passport',
  passportNumber: 'passportNumber',
  passportCountry: 'passportCountry',
  alienRegistration: 'alienRegistration',
};

export const LIVE_TOGETHER_FIELD_NAMES = {
  hasSamePreviousAddress: 'hasSamePreviousAddress',
  hasSameCurrentAddress: 'hasSameCurrentAddress',
  liveWithYouAtAddress: 'liveWithYouAtAddress',
  liveWithYouAtAnyAddresses: 'liveWithYouAtAnyAddresses',
  whichAddresses: 'whichAddresses',
  livedAnywhereElseLastTwoYears: 'livedAnywhereElseLastTwoYears',
};

export const RESIDENTIAL_HISTORY_FIELD_NAMES = {
  residence: 'residence',
  streetAddress: 'streetAddress',
  city: 'city',
  state: 'state',
  zipCode: 'zipCode',
  moveInDate: 'moveInDate',
  monthlyPayment: 'monthlyPayment',
  explainTheSituation: 'explainTheSituation',
  nameOfPersonYouLiveWith: 'nameOfPersonYouLiveWith',
  landlordCondoApartmentName: 'landlordCondoApartmentName',
  lender: 'lender',
  phoneNumber: 'phoneNumber',
  canContactToVerifySituation: 'canContactToVerifySituation',
  contactName: 'contactName',
  relationshipToYou: 'relationshipToYou',
  livedAnywhereElseLastTwoYears: 'livedAnywhereElseLastTwoYears',
  reasonForMoving: 'reasonForMoving',
  statesLived: 'statesLived',
  systemGeneratedStates: 'systemGeneratedStates',
  previousResidences: 'previousResidences',
};

export const BACKGROUND_FIELD_NAMES = {
  convicted: 'convicted', // yes,no
  convictedDescription: 'convictedDescription', // 'N/A' if convicted === 'yes' otherwise null
  bankruptcy: 'bankruptcy', // yes, no
  bankruptcyDescription: 'bankruptcyDescription', // 'N/A' if bankruptcy === 'yes' otherwise null
  bankruptcyCaseFiled: 'bankruptcyCaseFiled', // string, null
  bankruptcyDateFiled: 'bankruptcyDateFiled', // date, null
  bankruptcyDischarged: 'bankruptcyDischarged', // yes, no, null
  eviction: 'eviction', // yes, no
  offenderRegistration: 'offenderRegistration', // yes, no
};

export const BACKGROUND_FIELD_NAMES_LOWER_CASE = {
  [BACKGROUND_FIELD_NAMES.convicted]: BACKGROUND_FIELD_NAMES.convicted,
  [BACKGROUND_FIELD_NAMES.convictedDescription]:
    BACKGROUND_FIELD_NAMES.convictedDescription,
  [BACKGROUND_FIELD_NAMES.bankruptcy]: BACKGROUND_FIELD_NAMES.bankruptcy,
  [BACKGROUND_FIELD_NAMES.bankruptcyDescription]:
    BACKGROUND_FIELD_NAMES.bankruptcyDescription,
  [BACKGROUND_FIELD_NAMES.bankruptcyCaseFiled]:
    BACKGROUND_FIELD_NAMES.bankruptcyCaseFiled,
  [BACKGROUND_FIELD_NAMES.bankruptcyDateFiled]:
    BACKGROUND_FIELD_NAMES.bankruptcyDateFiled,
  [BACKGROUND_FIELD_NAMES.bankruptcyDischarged]:
    BACKGROUND_FIELD_NAMES.bankruptcyDischarged,
  [BACKGROUND_FIELD_NAMES.eviction]: BACKGROUND_FIELD_NAMES.eviction,
  [BACKGROUND_FIELD_NAMES.offenderRegistration]:
    BACKGROUND_FIELD_NAMES.offenderRegistration,
};

export const RESIDENCE_TYPES = {
  RENT: 'rent',
  OWN: 'own',
  LIVE_WITH_FAMILY_OR_FRIENDS: 'liveWithFamilyOrFriends',
  OTHER: 'other',
};

export const RESIDENCE_PERIODS = {
  currentResidence: 'currentResidence',
  previousResidence: 'previousResidence',
};

const BASE_FIELD_PROPS = {
  rules: {
    required: true,
  },
  GridProps: {
    xs: 12,
  },
};

export const IDENTIFICATION_FIELDS = {
  [IDENTIFICATION_FIELD_NAMES.socialSecurity]: ({
    name,
    member,
    selectedMembers,
    ...fieldProps
  }) => ({
    name,
    variant: 'socialSecurity',
    label: (
      <FormattedMessage
        id={
          'afforda.identifyingDetails.identification.inputLabel.socialSecurityNumber'
        }
        values={{ name: getFormattedName(member, selectedMembers) }}
      />
    ),
    InputLabelProps: isMinor(member)
      ? {
          TooltipProps: {
            // Add tooltip for minors
            title: (
              <FormattedMessage
                id="afforda.identifyingDetails.identification.tooltip.minorSSN"
                defaultMessage="If you are unable to provide the minor's SSN at this time, the response will default to 000-00-0000."
              />
            ),
            children: <AlertInfoIcon fontSize={'small'} />,
          },
        }
      : undefined,
    rules: {
      maxLength: MAX_SSN_LENGTH,
      minLength: MAX_SSN_LENGTH,
      required: !isMinor(member),
    },
    GridProps: {
      xs: 12,
    },
    ...fieldProps,
  }),
  [IDENTIFICATION_ALTERNATE_FIELD_NAMES.alternateId]: ({
    name,
    member,
    selectedMembers,
    intl,
    ...fieldProps
  }) => ({
    name,
    variant: 'buttonGroup',
    label: (
      <FormattedMessage
        id={
          'afforda.identifyingDetails.identificationAlternate.form.alternateIdLabel'
        }
        values={{ name: getFormattedName(member, selectedMembers, intl) }}
      />
    ),
    direction: 'row',
    options: [
      {
        value: IDENTIFICATION_ALTERNATE_FIELD_NAMES.passport,
        label: (
          <FormattedMessage
            id={
              'afforda.identifyingDetails.identificationAlternate.form.alternateId.passportLabel'
            }
          />
        ),
      },
      {
        value: IDENTIFICATION_ALTERNATE_FIELD_NAMES.alienRegistration,
        label: (
          <FormattedMessage
            id={
              'afforda.identifyingDetails.identificationAlternate.form.alternateId.alienRegistrationLabel'
            }
          />
        ),
      },
    ],
    ...BASE_FIELD_PROPS,
    rules: {
      required: !isMinor(member),
    },
    ...fieldProps,
  }),
  [IDENTIFICATION_ALTERNATE_FIELD_NAMES.passportNumber]: ({
    name,
    member,
    intl,
    hidden,
    watchFields,
    ...fieldProps
  }) => ({
    name,
    variant: 'text',
    label: (
      <FormattedMessage
        id={
          'afforda.identifyingDetails.identificationAlternate.form.passportNumberLabel'
        }
      />
    ),
    placeholder: intl.formatMessage({
      id: 'afforda.identifyingDetails.identificationAlternate.form.passportNumberLabel',
    }),
    hidden,
    watchFields,
    ...BASE_FIELD_PROPS,
    rules: {
      required: !isMinor(member),
      validate: validateNoSpecialChars,
      maxLength: 255, // From database migrations - character varying(255) | File path: fortress-backend/src/migrations
    },
    ...fieldProps,
  }),
  [IDENTIFICATION_ALTERNATE_FIELD_NAMES.passportCountry]: ({
    name,
    member,
    hidden,
    watchFields,
    options,
    ...fieldProps
  }) => ({
    name,
    variant: 'singleSelect',
    label: (
      <FormattedMessage
        id={
          'afforda.identifyingDetails.identificationAlternate.form.passportCountryLabel'
        }
      />
    ),
    options,
    disabled: !options?.length,
    hidden,
    watchFields,
    ...BASE_FIELD_PROPS,
    rules: {
      required: !isMinor(member),
    },
    ...fieldProps,
  }),
  [IDENTIFICATION_ALTERNATE_FIELD_NAMES.alienRegistration]: ({
    name,
    member,
    hidden,
    watchFields,
    intl,
    ...fieldProps
  }) => ({
    name,
    variant: 'text',
    label: (
      <FormattedMessage
        id={
          'afforda.identifyingDetails.identificationAlternate.form.alternateId.alienRegistrationLabel'
        }
      />
    ),
    placeholder: intl.formatMessage({
      id: 'afforda.identifyingDetails.identificationAlternate.form.alternateId.alienRegistrationLabel',
    }),
    ...BASE_FIELD_PROPS,
    rules: {
      required: !isMinor(member),
      maxLength: 255, // From database migrations - character varying(255) | File path: fortress-backend/src/migrations
    },
    hidden,
    watchFields,
    ...fieldProps,
  }),
};

const BASE_RESIDENTIAL_HISTORY_FIELD_PROPS = {
  rules: {
    required: true,
  },
  GridProps: {
    sm: 6,
    xs: 12,
  },
  watchFields: [RESIDENTIAL_HISTORY_FIELD_NAMES.residence],
  hidden: ({ getValues }) =>
    !Boolean(getValues(RESIDENTIAL_HISTORY_FIELD_NAMES.residence)?.length),
};

export const RESIDENTIAL_HISTORY_FIELDS = {
  [RESIDENTIAL_HISTORY_FIELD_NAMES.streetAddress]: ({
    intl,
    setValue,
    error,
    helperText,
    ...fieldProps
  }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.streetAddress,
    variant: 'address',
    label: intl.formatMessage({
      id: 'app.label.streetAddress',
    }),
    placeholder: intl.formatMessage({
      id: 'app.label.streetAddress',
    }),
    error,
    helperText,
    onChange: ({ target: { value, addressComponents } }) => {
      if (addressComponents) {
        const { city, state, zipCode } = addressComponents;
        setValue(RESIDENTIAL_HISTORY_FIELD_NAMES.city, city);
        setValue(RESIDENTIAL_HISTORY_FIELD_NAMES.state, state);
        setValue(RESIDENTIAL_HISTORY_FIELD_NAMES.zipCode, zipCode);
      }

      setValue(RESIDENTIAL_HISTORY_FIELD_NAMES.streetAddress, value);
    },
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    ...fieldProps,
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.city]: ({ intl, ...fieldProps }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.city,
    variant: 'text',
    label: <FormattedMessage id={'app.label.city'} />,
    placeholder: intl.formatMessage({
      id: 'app.label.city',
    }),
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    rules: {
      ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS.rules,
      validate: validateNoSpecialChars,
      maxLength: 255, // From database migrations - character varying(255) | File path: fortress-backend/src/migrations
    },
    ...fieldProps,
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.state]: ({
    intl,
    states,
    ...fieldProps
  }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.state,
    variant: 'singleSelect',
    label: intl.formatMessage({
      id: 'app.label.stateOrProvince',
    }),
    placeholder: intl.formatMessage({
      id: 'app.label.stateOrProvince',
    }),
    options: states?.map((state) => ({
      value: state.code,
      text: state.name,
    })),
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    rules: {
      ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS.rules,
      required: true,
    },
    ...fieldProps,
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.zipCode]: ({ intl, ...fieldProps }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.zipCode,
    variant: 'number',
    label: intl.formatMessage({
      id: 'app.label.zip',
    }),
    placeholder: intl.formatMessage({
      id: 'app.label.zip',
    }),
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    ...fieldProps,
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.moveInDate]: ({
    intl,
    maxMoveInDate,
    ...fieldProps
  }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.moveInDate,
    variant: 'date',
    label: intl.formatMessage({
      id: 'app.label.moveInDate',
    }),
    disableFuture: true,
    maxDate: maxMoveInDate,
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    ...fieldProps,
    rules: {
      ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS.rules,
    },
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.monthlyPayment]: ({
    intl,
    ...fieldProps
  }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.monthlyPayment,
    variant: 'number',
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.residentialHistory.label.monthlyPayment',
    }),
    adornment: <MoneyIcon />,
    placeholder: '0.00',
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    ...fieldProps,
    rules: {
      ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS.rules,
      // Monthly Payment is required for all residence scenarios and should accept 0
      required: true,
    },
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.explainTheSituation]: ({
    intl,
    hidden,
    ...fieldProps
  }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.explainTheSituation,
    variant: 'text',
    multiline: true,
    rows: 3,
    maxLength: 255, // From applicantForm.schema.js - general text fields limited to 255 characters | File path: fortress-backend/src/domain/validators/applicantForm.schema.js
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.residentialHistory.label.explainSituation',
    }),
    placeholder: intl.formatMessage({
      id: 'afforda.identifyingDetails.residentialHistory.label.explainSituation.placeholder',
    }),
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    watchFields: getExplainTheSituationWatchFields(fieldProps.name),
    rules: {
      ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS.rules,
      validate: (value, getValues) => {
        if (!value) {
          return true;
        }
        const lengthError = getExplainTheSituationLengthError({
          getValues,
          intl,
          name: fieldProps.name,
          value,
        });
        if (lengthError) {
          return lengthError;
        }
        return validateNoSpecialChars(value);
      },
    },
    ...fieldProps,
    hidden,
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.nameOfPersonYouLiveWith]: ({
    intl,
    hidden,
    ...fieldProps
  }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.nameOfPersonYouLiveWith,
    variant: 'text',
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.residentialHistory.label.nameOfPersonYouLiveWith',
    }),
    placeholder: intl.formatMessage({ id: 'app.placeholder.name' }),
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    GridProps: {
      xs: 12,
    },
    rules: {
      ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS.rules,
      validate: validateNoSpecialChars,
      maxLength: 35, // From applicantForm.schema.js - firstName.max(35) | File path: fortress-backend/src/domain/validators/applicantForm.schema.js
    },
    ...fieldProps,
    hidden,
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.landlordCondoApartmentName]: ({
    intl,
    hidden,
    ...fieldProps
  }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.landlordCondoApartmentName,
    variant: 'text',
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.residentialHistory.label.landlordCondoApartmentName',
    }),
    placeholder: intl.formatMessage({
      id: 'app.placeholder.name',
    }),
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    rules: {
      ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS.rules,
      validate: validateNoSpecialChars,
      maxLength: 255, // From database migrations - character varying(255) | File path: fortress-backend/src/migrations
    },
    ...fieldProps,
    hidden,
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.lender]: ({
    intl,
    hidden,
    ...fieldProps
  }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.lender,
    variant: 'text',
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.residentialHistory.label.lender',
    }),
    placeholder: intl.formatMessage({
      id: 'app.placeholder.name',
    }),
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    rules: {
      ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS.rules,
      validate: validateNoSpecialChars,
      maxLength: 255, // From database migrations - character varying(255) | File path: fortress-backend/src/migrations
    },
    ...fieldProps,
    hidden,
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.phoneNumber]: ({
    intl,
    hidden,
    watchFields,
    ...fieldProps
  }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.phoneNumber,
    variant: 'phone',
    label: intl.formatMessage({
      id: 'app.label.phoneNumber',
    }),
    placeholder: intl.formatMessage({
      id: 'app.label.phoneNumber',
    }),
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    watchFields,
    hidden,
    ...fieldProps,
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.canContactToVerifySituation]: ({
    intl,
    hidden,
    ...fieldProps
  }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.canContactToVerifySituation,
    variant: 'buttonGroup',
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.residentialHistory.label.canContactToVerifySituation',
    }),
    direction: 'row',
    options: YES_NO_OPTIONS,
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    GridProps: {
      xs: 12,
    },
    hidden,
    ...fieldProps,
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.contactName]: ({
    intl,
    hidden,
    ...fieldProps
  }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.contactName,
    variant: 'text',
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.residentialHistory.label.contactName',
    }),
    placeholder: intl.formatMessage({
      id: 'app.placeholder.name',
    }),
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    watchFields: [
      RESIDENTIAL_HISTORY_FIELD_NAMES.residence,
      RESIDENTIAL_HISTORY_FIELD_NAMES.canContactToVerifySituation,
    ],
    hidden,
    ...fieldProps,
    rules: {
      ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS.rules,
      validate: validateNoSpecialChars,
      maxLength: 35, // From applicantForm.schema.js - name fields limited to 35 characters | File path: fortress-backend/src/domain/validators/applicantForm.schema.js
    },
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.relationshipToYou]: ({
    intl,
    hidden,
    ...fieldProps
  }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.relationshipToYou,
    variant: 'text',
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.residentialHistory.label.relationshipToYou',
    }),
    placeholder: intl.formatMessage({
      id: 'afforda.identifyingDetails.residentialHistory.label.relationshipToYou.placeholder',
    }),
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    rules: {
      ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS.rules,
      validate: validateNoSpecialChars,
      maxLength: 255, // From database migrations - character varying(255) | File path: fortress-backend/src/migrations
    },
    GridProps: {
      xs: 12,
      sm: 6,
    },
    watchFields: [
      RESIDENTIAL_HISTORY_FIELD_NAMES.residence,
      RESIDENTIAL_HISTORY_FIELD_NAMES.canContactToVerifySituation,
    ],
    hidden,
    ...fieldProps,
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.reasonForMoving]: ({
    intl,
    ...fieldProps
  }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.reasonForMoving,
    variant: 'text',
    multiline: true,
    rows: 2,
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.residentialHistory.label.reasonForMoving',
    }),
    placeholder: intl.formatMessage({
      id: 'afforda.identifyingDetails.residentialHistory.label.reasonForMoving.placeholder',
    }),
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    GridProps: {
      xs: 12,
    },
    rules: {
      ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS.rules,
      validate: (value) => {
        if (!value) {
          return true;
        }
        if (value.length > 255) {
          return intl.formatMessage({
            id: 'app.toast.error.situation.maxLength',
          });
        }
        return validateNoSpecialChars(value);
      },
    },
    ...fieldProps,
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.livedAnywhereElseLastTwoYears]: ({
    intl,
    ...fieldProps
  }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.livedAnywhereElseLastTwoYears,
    variant: 'buttonGroup',
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.residentialHistory.label.livedAnywhereElseLastTwoYears',
    }),
    direction: 'row',
    options: YES_NO_OPTIONS,
    ...BASE_RESIDENTIAL_HISTORY_FIELD_PROPS,
    GridProps: {
      xs: 12,
    },
    ...fieldProps,
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.systemGeneratedStates]: ({
    intl,
    states,
    shouldShowStatesLived,
    statesLived,
  }) => ({
    name: RESIDENTIAL_HISTORY_FIELD_NAMES.systemGeneratedStates,
    variant: 'custom',
    readOnly: true,
    GridProps: { xs: 12 },
    hidden: !shouldShowStatesLived,
    component: (
      <>
        <Typography variant="labelLarge" mb={2} display="block">
          {intl.formatMessage({
            id: 'afforda.identifying.statesLived.instruction',
          })}
        </Typography>
        <Typography variant="labelLarge" display="block">
          {intl.formatMessage({
            id: 'afforda.identifying.statesLived.systemGenerated',
          })}
          :
        </Typography>
        {statesLived?.length > 0 ? (
          <Box component="ul" sx={{ margin: '8px 0', paddingLeft: '20px' }}>
            {statesLived.sort().map((state) => {
              const stateName = states.find((s) => s.code === state)?.name;
              return (
                <Typography
                  key={state}
                  component="li"
                  variant="bodyMedium"
                  display="block"
                >
                  {stateName}
                </Typography>
              );
            })}
          </Box>
        ) : (
          <Typography
            variant="bodyMedium"
            sx={{ margin: '8px 0' }}
            display="block"
          >
            {intl.formatMessage({
              id: 'afforda.identifying.statesLived.noStatesAutoSelected',
            })}
          </Typography>
        )}
      </>
    ),
  }),
  [RESIDENTIAL_HISTORY_FIELD_NAMES.statesLived]: ({
    intl,
    states,
    lockedStates = [],
    shouldShowStatesLived,
    ...fieldProps
  }) => {
    return {
      name: RESIDENTIAL_HISTORY_FIELD_NAMES.statesLived,
      variant: 'multiSelect',
      label: intl.formatMessage({
        id: 'afforda.identifying.statesLived.label',
      }),
      options: states?.map((state) => ({
        value: state.code,
        text: state.name,
        disabled: lockedStates.includes(state.code),
        tooltip: lockedStates.includes(state.code)
          ? intl.formatMessage({
              id: 'afforda.identifying.statesLived.lockedTooltip',
            })
          : undefined,
      })),
      rules: {
        required: true,
      },
      GridProps: {
        xs: 12,
      },
      hidden: !shouldShowStatesLived,
      ...fieldProps,
    };
  },
};
const SHOULD_SHOW_CONVICTED_ADDITIONAL_FIELDS = (namePrefix) => ({
  watchFields: [
    namePrefix
      ? `${namePrefix}.${BACKGROUND_FIELD_NAMES.convicted}`
      : BACKGROUND_FIELD_NAMES.convicted,
  ],
  hidden: ({ getValues }) =>
    getValues(
      namePrefix
        ? `${namePrefix}.${BACKGROUND_FIELD_NAMES.convicted}`
        : BACKGROUND_FIELD_NAMES.convicted,
    ) !== 'yes',
});

const SHOULD_SHOW_BANKRUPTCY_ADDITIONAL_FIELDS = (namePrefix) => ({
  watchFields: [
    namePrefix
      ? `${namePrefix}.${BACKGROUND_FIELD_NAMES.bankruptcy}`
      : BACKGROUND_FIELD_NAMES.bankruptcy,
  ],
  hidden: ({ getValues }) =>
    getValues(
      namePrefix
        ? `${namePrefix}.${BACKGROUND_FIELD_NAMES.bankruptcy}`
        : BACKGROUND_FIELD_NAMES.bankruptcy,
    ) !== 'yes',
});

export const BACKGROUND_FIELDS = {
  [BACKGROUND_FIELD_NAMES.convicted]: ({
    intl,
    namePrefix,
    ...fieldProps
  }) => ({
    name: namePrefix
      ? `${namePrefix}.${BACKGROUND_FIELD_NAMES.convicted}`
      : BACKGROUND_FIELD_NAMES.convicted,
    variant: 'buttonGroup',
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.background.label.convicted',
    }),
    direction: 'row',
    options: YES_NO_OPTIONS,
    ...BASE_FIELD_PROPS,
    ...fieldProps,
  }),
  [BACKGROUND_FIELD_NAMES.convictedDescription]: ({
    intl,
    namePrefix,
    ...fieldProps
  }) => ({
    name: namePrefix
      ? `${namePrefix}.${BACKGROUND_FIELD_NAMES.convictedDescription}`
      : BACKGROUND_FIELD_NAMES.convictedDescription,
    variant: 'text',
    multiline: true,
    rows: 3,
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.background.label.convictedDescription',
    }),
    placeholder: intl.formatMessage({
      id: 'afforda.identifyingDetails.background.placeholder.convictedDescription',
    }),
    ...BASE_FIELD_PROPS,
    ...SHOULD_SHOW_CONVICTED_ADDITIONAL_FIELDS(namePrefix),
    ...fieldProps,
    rules: {
      validate: validateNoSpecialChars,
    },
  }),
  [BACKGROUND_FIELD_NAMES.bankruptcy]: ({
    intl,
    namePrefix,
    ...fieldProps
  }) => ({
    name: namePrefix
      ? `${namePrefix}.${BACKGROUND_FIELD_NAMES.bankruptcy}`
      : BACKGROUND_FIELD_NAMES.bankruptcy,
    variant: 'buttonGroup',
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.background.label.bankruptcy',
    }),
    direction: 'row',
    options: YES_NO_OPTIONS,
    ...BASE_FIELD_PROPS,
    ...fieldProps,
  }),
  [BACKGROUND_FIELD_NAMES.bankruptcyDescription]: ({
    intl,
    namePrefix,
    ...fieldProps
  }) => ({
    name: namePrefix
      ? `${namePrefix}.${BACKGROUND_FIELD_NAMES.bankruptcyDescription}`
      : BACKGROUND_FIELD_NAMES.bankruptcyDescription,
    variant: 'text',
    multiline: true,
    rows: 3,
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.background.label.bankruptcyDescription',
    }),
    placeholder: intl.formatMessage({
      id: 'afforda.identifyingDetails.background.placeholder.bankruptcyDescription',
    }),
    ...BASE_FIELD_PROPS,
    ...SHOULD_SHOW_BANKRUPTCY_ADDITIONAL_FIELDS(namePrefix),
    ...fieldProps,
    rules: {
      validate: validateNoSpecialChars,
    },
  }),
  [BACKGROUND_FIELD_NAMES.bankruptcyCaseFiled]: ({
    intl,
    namePrefix,
    ...fieldProps
  }) => ({
    name: namePrefix
      ? `${namePrefix}.${BACKGROUND_FIELD_NAMES.bankruptcyCaseFiled}`
      : BACKGROUND_FIELD_NAMES.bankruptcyCaseFiled,
    variant: 'text',
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.background.label.bankruptcyTypeFiled',
    }),
    placeholder: intl.formatMessage({
      id: 'afforda.identifyingDetails.background.placeholder.bankruptcyTypeFiled',
    }),
    ...BASE_FIELD_PROPS,
    ...SHOULD_SHOW_BANKRUPTCY_ADDITIONAL_FIELDS(namePrefix),
    ...fieldProps,
    rules: {
      validate: validateNoSpecialChars,
      required: true,
    },
  }),
  [BACKGROUND_FIELD_NAMES.bankruptcyDateFiled]: ({
    intl,
    namePrefix,
    fieldProps,
  }) => ({
    name: namePrefix
      ? `${namePrefix}.${BACKGROUND_FIELD_NAMES.bankruptcyDateFiled}`
      : BACKGROUND_FIELD_NAMES.bankruptcyDateFiled,
    variant: 'date',
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.background.label.bankruptcyDateFiled',
    }),
    disableFuture: true,
    ...BASE_FIELD_PROPS,
    ...SHOULD_SHOW_BANKRUPTCY_ADDITIONAL_FIELDS(namePrefix),
    ...fieldProps,
    rules: {
      required: true,
    },
  }),
  [BACKGROUND_FIELD_NAMES.bankruptcyDischarged]: ({
    intl,
    namePrefix,
    fieldProps,
  }) => ({
    name: namePrefix
      ? `${namePrefix}.${BACKGROUND_FIELD_NAMES.bankruptcyDischarged}`
      : BACKGROUND_FIELD_NAMES.bankruptcyDischarged,
    variant: 'buttonGroup',
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.background.label.bankruptcyDischarged',
    }),
    direction: 'row',
    options: YES_NO_OPTIONS,
    ...BASE_FIELD_PROPS,
    ...SHOULD_SHOW_BANKRUPTCY_ADDITIONAL_FIELDS(namePrefix),
    ...fieldProps,
  }),
  [BACKGROUND_FIELD_NAMES.eviction]: ({
    intl,
    namePrefix,
    isAffordableUser,
    ...fieldProps
  }) => ({
    name: namePrefix
      ? `${namePrefix}.${BACKGROUND_FIELD_NAMES.eviction}`
      : BACKGROUND_FIELD_NAMES.eviction,
    variant: 'buttonGroup',
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.background.label.eviction',
    }),
    hidden: !isAffordableUser,
    direction: 'row',
    options: YES_NO_OPTIONS,
    ...BASE_FIELD_PROPS,
    ...fieldProps,
  }),
  [BACKGROUND_FIELD_NAMES.offenderRegistration]: ({
    intl,
    namePrefix,
    ...fieldProps
  }) => ({
    name: namePrefix
      ? `${namePrefix}.${BACKGROUND_FIELD_NAMES.offenderRegistration}`
      : BACKGROUND_FIELD_NAMES.offenderRegistration,
    variant: 'buttonGroup',
    label: intl.formatMessage({
      id: 'afforda.identifyingDetails.background.label.offenderRegistration',
    }),
    direction: 'row',
    options: YES_NO_OPTIONS,
    ...BASE_FIELD_PROPS,
    ...fieldProps,
  }),
};

export const IDENTIFYING_DETAILS_SECTION_NAMES = {
  OVERVIEW: 'overview',
  IDENTIFICATION: 'identification',
  IDENTIFICATION_ALTERNATE: 'identificationAlternate',
  HUD_CITIZENSHIP: 'hudCitizenship',
  RESIDENTIAL_HISTORY_CURRENT: 'residentialHistoryCurrent',
  RESIDENTIAL_HISTORY_PREVIOUS: 'residentialHistoryPrevious',
  LIVE_TOGETHER_CURRENT: 'liveTogetherCurrent',
  LIVE_TOGETHER_PREVIOUS: 'liveTogetherPrevious',
  BACKGROUND: 'background',
  SUMMARY: 'summary',
};

// Only adding optional forms steps fields here. Can change this later if needed.
export const SECTION_PATHS = {
  overview: `${PATHS.IDENTIFYING_DETAILS}/${IDENTIFYING_DETAILS_PATHS.OVERVIEW}`,
  identification: `${PATHS.IDENTIFYING_DETAILS}/${IDENTIFYING_DETAILS_PATHS.IDENTIFICATION}`,
  identificationAlternate: `${PATHS.IDENTIFYING_DETAILS}/${IDENTIFYING_DETAILS_PATHS.IDENTIFICATION_ALTERNATE}`,
  hudCitizenship: `${PATHS.IDENTIFYING_DETAILS}/${IDENTIFYING_DETAILS_PATHS.HUD_CITIZENSHIP}`,
  residentialHistoryCurrent: `${PATHS.IDENTIFYING_DETAILS}/${IDENTIFYING_DETAILS_PATHS.RESIDENTIAL_HISTORY}/${RESIDENTIAL_HISTORY_PATHS.CURRENT}`,
  residentialHistoryPrevious: `${PATHS.IDENTIFYING_DETAILS}/${IDENTIFYING_DETAILS_PATHS.RESIDENTIAL_HISTORY}/${RESIDENTIAL_HISTORY_PATHS.PREVIOUS}`,
  liveTogetherCurrent: `${PATHS.IDENTIFYING_DETAILS}/${RESIDENTIAL_HISTORY_PATHS.LIVE_TOGETHER}/${RESIDENTIAL_HISTORY_PATHS.CURRENT}`,
  liveTogetherPrevious: `${PATHS.IDENTIFYING_DETAILS}/${RESIDENTIAL_HISTORY_PATHS.LIVE_TOGETHER}/${RESIDENTIAL_HISTORY_PATHS.PREVIOUS}`,
  background: `${PATHS.IDENTIFYING_DETAILS}/${IDENTIFYING_DETAILS_PATHS.BACKGROUND}`,
  summary: `${PATHS.IDENTIFYING_DETAILS}/${IDENTIFYING_DETAILS_PATHS.SUMMARY}`,
};

export const IDENTIFYING_DETAILS_PATHS_TO_FIELD_NAMES_MAP = {
  [IDENTIFYING_DETAILS_PATHS.IDENTIFICATION]: IDENTIFICATION_FIELD_NAMES,
  [IDENTIFYING_DETAILS_PATHS.IDENTIFICATION_ALTERNATE]:
    IDENTIFICATION_ALTERNATE_FIELD_NAMES,
  [IDENTIFYING_DETAILS_PATHS.HUD_CITIZENSHIP]: HUD_CITIZENSHIP_FIELD_NAMES,
  [IDENTIFYING_DETAILS_PATHS.BACKGROUND]: BACKGROUND_FIELD_NAMES,
  [SECTION_PATHS.residentialHistoryCurrent]: RESIDENTIAL_HISTORY_FIELD_NAMES,
  [SECTION_PATHS.residentialHistoryPrevious]: RESIDENTIAL_HISTORY_FIELD_NAMES,
  [SECTION_PATHS.liveTogetherCurrent]: LIVE_TOGETHER_FIELD_NAMES,
  [SECTION_PATHS.liveTogetherPrevious]: LIVE_TOGETHER_FIELD_NAMES,
};

// HUD Citizenship field definitions
export const HUD_CITIZENSHIP_FIELDS = {
  [HUD_CITIZENSHIP_FIELD_NAMES.affordableCitizenStatusId]: ({
    name,
    member,
    selectedMembers,
    options,
    intl,
    ...fieldProps
  }) => ({
    name,
    variant: 'singleSelect',
    label: 'Citizen Status',
    options,
    placeholder: 'Choose',
    rules: {
      required: true,
    },
    GridProps: {
      xs: 12,
    },
    helperText: (
      <FormattedMessage
        id="afforda.selfIdentification.hudCitizenship.citizenshipStatus.label"
        values={{ name: getFormattedName(member, selectedMembers, intl) }}
      />
    ),
    ...fieldProps,
  }),
  [HUD_CITIZENSHIP_FIELD_NAMES.affordableSSNExceptionId]: ({
    name,
    member,
    selectedMembers,
    options,
    intl,
    ...fieldProps
  }) => ({
    name,
    variant: 'singleSelect',
    label: 'SSN Exceptions',
    options,
    placeholder: 'Choose',
    rules: {
      required: true,
    },
    GridProps: {
      xs: 12,
    },
    helperText: (
      <FormattedMessage
        id="afforda.selfIdentification.hudCitizenship.ssnException.label"
        values={{ name: getFormattedName(member, selectedMembers, intl) }}
      />
    ),
    ...fieldProps,
  }),
  [HUD_CITIZENSHIP_FIELD_NAMES.ssnExceptionsExplanation]: ({
    name,
    member,
    selectedMembers,
    ssnExemptionsInfoList,
    ...fieldProps
  }) => ({
    name,
    variant: 'custom',
    component: (
      <Typography variant={'bodySmall'}>
        <FormattedMessage id="afforda.identifyingDetails.hudCitizenship.ssnExemptions.title" />
        <List inlist={true} sx={{ listStyle: 'outside', paddingLeft: 2 }}>
          {ssnExemptionsInfoList?.map((exception) => (
            <Typography
              component={'li'}
              key={exception.id}
              variant={'bodySmall'}
            >
              <Typography variant={'bodySmall'} fontWeight={'bold'}>
                {exception.code}
              </Typography>
              : {exception.name}
            </Typography>
          ))}
        </List>
      </Typography>
    ),
    GridProps: {
      xs: 12,
    },
    ...fieldProps,
  }),
};
